<div class="container mx-auto px-4 py-8 max-w-7xl">
  <div class="mb-6">
    <h1 class="text-3xl font-bold text-gray-900 mb-2">Wettkampftermine</h1>
    <p class="text-gray-600">Termine für Kreismeisterschaft 2026 (nicht Wettkampfende)</p>
  </div>

  <!-- Loading State -->
  @if (isLoading()) {
    <div class="text-center py-8">
      <p class="text-slate-600">Lade Wettkampftermine...</p>
    </div>
  }

  <!-- Error State -->
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6">
      {{ error() }}
    </div>
  }

  <!-- Search -->
  @if (!isLoading() && !error()) {
    <div class="mb-6">
      <div class="relative">
        <input
          type="text"
          [value]="searchTerm()"
          (input)="onSearchChange($any($event.target).value)"
          placeholder="Suchen nach Disziplin, Anlage, Klasse..."
          class="w-full px-4 py-3 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />
        @if (searchTerm()) {
          <button
            (click)="clearSearch()"
            class="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-sm text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        }
      </div>
      <p class="mt-2 text-sm text-gray-500">
        {{ filteredCompetitions().length }} von {{ competitions().length }} Einträgen
      </p>
    </div>

  <!-- Desktop Table -->
  <div class="hidden lg:block overflow-x-auto shadow-md rounded-lg">
    <table class="w-full bg-white">
      <thead class="bg-gray-50 border-b-2 border-gray-200">
      <tr>
        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">
          Disziplinen
        </th>
        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">
          Klassen
        </th>
        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">
          Anlage
        </th>
        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">
          Datum und Startzeit (nicht Wettkampfende)
        </th>
      </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
        @for (competition of filteredCompetitions(); track $index) {
          <tr class="hover:bg-gray-50">
            <td class="px-6 py-4 text-sm text-gray-900">
              @for (disziplin of competition.disziplinen; track disziplin.code) {
                <div class="mb-1">
                  <span class="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{{ disziplin.code }}</span>
                  <span class="ml-2">{{ disziplin.name }}</span>
                </div>
              }
            </td>
            <td class="px-6 py-4 text-sm text-gray-900">
              {{ competition.klassen }}
            </td>
            <td class="px-6 py-4 text-sm text-gray-900">
              @for (anlage of competition.anlagen; track anlage) {
                <div>{{ anlage }}</div>
              }
            </td>
            <td class="px-6 py-4 text-sm text-gray-900">
              @for (termin of competition.termine; track termin.datum) {
                <div class="mb-2">
                  <div class="font-medium">
                    {{ termin.wochentag }} {{ formatDate(termin.datum) }} von {{ termin.startzeit }} bis {{ termin.endzeit }}
                  </div>
                  @if (termin.anlage && competition.anlagen.length > 1) {
                    <div class="text-xs text-gray-600">{{ termin.anlage }}</div>
                  }
                  @if (termin.anmerkung) {
                    <div class="text-xs text-gray-500 mt-1 italic">{{ termin.anmerkung }}</div>
                  }
                </div>
              }
              @if (competition.anmerkungen.length > 0) {
                <div class="mt-2 pt-2 border-t border-gray-200">
                  @for (anmerkung of competition.anmerkungen; track $index) {
                    <div class="text-xs text-blue-600 italic">ℹ {{ anmerkung }}</div>
                  }
                </div>
              }
            </td>
          </tr>
        }
      </tbody>
    </table>

    @if (filteredCompetitions().length === 0) {
      <div class="bg-white p-12 text-center">
        <p class="text-gray-500">Keine Ergebnisse gefunden</p>
      </div>
    }
  </div>

  <!-- Mobile Cards -->
  <div class="lg:hidden space-y-4">
    @for (competition of filteredCompetitions(); track $index) {
      <div class="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div class="mb-3">
          <h3 class="font-bold text-lg text-gray-900 mb-2">Disziplinen</h3>
          @for (disziplin of competition.disziplinen; track disziplin.code) {
            <div class="mb-1">
              <span class="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{{ disziplin.code }}</span>
              <span class="ml-2 text-sm">{{ disziplin.name }}</span>
            </div>
          }
        </div>

        <div class="space-y-2 text-sm">
          <div class="text-gray-700">
            <span class="font-semibold">Klassen:</span> {{ competition.klassen }}
          </div>

          <div class="text-gray-700">
            <span class="font-semibold">Anlagen:</span>
            @for (anlage of competition.anlagen; track anlage) {
              <span class="inline-block mr-2">{{ anlage }}</span>
            }
          </div>

          <div class="mt-3 pt-3 border-t border-gray-200">
            <div class="font-semibold mb-2">Termine:</div>
            @for (termin of competition.termine; track termin.datum) {
              <div class="mb-3 pl-2 border-l-2 border-blue-500">
                <div class="font-medium text-gray-900">
                  {{ termin.wochentag }} {{ formatDate(termin.datum) }}
                </div>
                <div class="text-gray-600">
                  {{ termin.startzeit }} – {{ termin.endzeit }}
                </div>
                @if (termin.anlage && competition.anlagen.length > 1) {
                  <div class="text-xs text-gray-600 mt-1">{{ termin.anlage }}</div>
                }
                @if (termin.anmerkung) {
                  <div class="text-xs text-gray-500 mt-1 italic">{{ termin.anmerkung }}</div>
                }
              </div>
            }
          </div>

          @if (competition.anmerkungen.length > 0) {
            <div class="mt-3 pt-3 border-t border-gray-200">
              @for (anmerkung of competition.anmerkungen; track $index) {
                <div class="text-xs text-blue-600 italic mb-1">ℹ {{ anmerkung }}</div>
              }
            </div>
          }
        </div>
      </div>
    }

    @if (filteredCompetitions().length === 0) {
      <div class="bg-white rounded-lg shadow-md p-8 text-center">
        <p class="text-gray-500">Keine Ergebnisse</p>
      </div>
    }
  </div>
  }
</div>
